import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { INDUSTRIES } from '@/constants/industries';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import {
    ArrowLeft, Building2, Briefcase, MapPin, Phone, Mail, Calendar,
    ClipboardCheck, Award, Clock, FileCheck, AlertCircle, ChevronRight,
    Edit2, Save, X, Loader2, IdCard, Users, MapPinned, KeyRound, Trash2, AlertTriangle,
    Sparkles, CalendarPlus, ShieldAlert,
} from 'lucide-react';
import ResetPasswordModal from '@/components/ResetPasswordModal';
import ChecklistPreviewModal from '@/components/ChecklistPreviewModal';
import EmailComposerModal from '@/components/EmailComposerModal';
import ClientCommunicationsPanel from '@/components/ClientCommunicationsPanel';
import ClientMediaPanel from '@/components/ClientMediaPanel';
import { useAuth } from '@/context/AuthContext';
import WorkShiftsField, { WorkShiftsSummary } from '@/components/WorkShiftsField';
import { getDisplayTitle } from '@/lib/displayTitle';

const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{2,3}$/i;
const PROPERTY_STATUS_OPTIONS = ['Propio', 'Arrendado', 'En comodato', 'Prestado', 'Otro'];
function formatDateTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-MX');
}

const VIOLET = '#7C6BEF';
const STATUS_STYLES = {
    pending: { label: 'Pendiente', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
    assigned: { label: 'Asignada', color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
    in_progress: { label: 'En curso', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)' },
    in_review: { label: 'En revisión', color: '#EAB308', bg: 'rgba(234,179,8,0.15)' },
    approved: { label: 'Aprobada', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
    rejected: { label: 'Rechazada', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
};

const DONUT_COLORS = {
    pendientes: '#F59E0B',
    en_curso: '#8B5CF6',
    en_revision: '#EAB308',
    aprobadas: '#10B981',
    rechazadas: '#EF4444',
};

const DONUT_LABELS = {
    pendientes: 'Pendientes',
    en_curso: 'En curso',
    en_revision: 'En revisión',
    aprobadas: 'Aprobadas',
    rechazadas: 'Rechazadas',
};

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    // Currently-logged-in admin (session). We use this for defense-in-depth on
    // admin-only shortcuts. The route is already wrapped in
    // <ProtectedRoute role="admin"> in App.js so non-admins cannot reach this
    // component in the first place, but we guard again below to make the
    // intent explicit for future refactors.
    const { user: sessionUser } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [geoLoading, setGeoLoading] = useState(false);
    const [showResetPw, setShowResetPw] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showChecklistPreview, setShowChecklistPreview] = useState(false);
    const [showEmailComposer, setShowEmailComposer] = useState(false);
    const [activeTab, setActiveTab] = useState('audits'); // 'audits' | 'communications'
    const [rubros, setRubros] = useState([]);
    const [distintivos, setDistintivos] = useState([]);

    useEffect(() => {
        api.get('/rubros').then((r) => setRubros(r.data || [])).catch(() => setRubros([]));
        // Iter 51 Fase D · switched to distintivos (bundle picker)
        api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([]));
    }, []);

    const rubroOptions = rubros.length > 0
        ? rubros.map((r) => r.name).sort((a, b) => a.localeCompare(b))
        : INDUSTRIES;

    const load = () => {
        setLoading(true);
        api.get(`/admin/clients/${id}/detail`)
            .then((r) => { setData(r.data); setForm(r.data?.user || {}); })
            .catch((e) => setError(apiErrorMessage(e, 'No se pudo cargar el detalle')))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    // Iter 76 · Issue 4/10 · el Distintivo comercial es el título principal.
    const distLookup = useMemo(() => Object.fromEntries(distintivos.map((d) => [d.id, d])), [distintivos]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const startEdit = () => { setForm(data?.user || {}); setEditing(true); };
    const cancelEdit = () => { setForm(data?.user || {}); setEditing(false); };

    const [unlocking, setUnlocking] = useState(false);
    const unlock = async () => {
        setUnlocking(true);
        try {
            await api.post(`/admin/users/${id}/unlock`);
            toast.success('Cuenta desbloqueada');
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo desbloquear'));
        } finally { setUnlocking(false); }
    };

    const captureGeo = () => {
        if (!navigator.geolocation) { toast.error('Geolocalización no soportada'); return; }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                set('latitude', parseFloat(pos.coords.latitude.toFixed(6)));
                set('longitude', parseFloat(pos.coords.longitude.toFixed(6)));
                toast.success('Ubicación capturada');
                setGeoLoading(false);
            },
            (err) => { toast.error(`Error GPS: ${err.message}`); setGeoLoading(false); },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const save = async () => {
        const rfc = (form.rfc || '').trim().toUpperCase();
        if (rfc && !RFC_RE.test(rfc)) {
            toast.error('RFC inválido (persona moral 12 caracteres o física 13)');
            return;
        }
        setSaving(true);
        try {
            const lat = form.latitude !== '' && form.latitude !== null && !isNaN(parseFloat(form.latitude))
                ? parseFloat(form.latitude) : null;
            const lng = form.longitude !== '' && form.longitude !== null && !isNaN(parseFloat(form.longitude))
                ? parseFloat(form.longitude) : null;
            const patch = {
                name: form.name || '',
                company: form.company || null,
                trade_name: form.trade_name || null,
                rfc: rfc || null,
                industry: form.industry || null,
                phone: form.phone || null,
                address: form.address || null,
                fiscal_address: form.fiscal_address || null,
                city: form.city || null,
                state: form.state || null,
                country: form.country || null,
                latitude: lat,
                longitude: lng,
                contact_person: form.contact_person || null,
                legal_representative: form.legal_representative || null,
                general_manager: form.general_manager || null,
                property_status: form.property_status || null,
                work_shifts: form.work_shifts || null,
                notes: form.notes || null,
                active: !!form.active,
                num_habitaciones: form.num_habitaciones === '' || form.num_habitaciones == null ? null : Number(form.num_habitaciones),
                num_mesas: form.num_mesas === '' || form.num_mesas == null ? null : Number(form.num_mesas),
                capacity_collaborators: form.capacity_collaborators === '' || form.capacity_collaborators == null ? null : Number(form.capacity_collaborators),
                m2_construccion: form.m2_construccion === '' || form.m2_construccion == null ? null : Number(form.m2_construccion),
                desired_certification_ids: Array.isArray(form.desired_certification_ids) ? form.desired_certification_ids : [],
                desired_distintivo_ids: Array.isArray(form.desired_distintivo_ids) ? form.desired_distintivo_ids : [],
            };
            await api.patch(`/admin/users/${id}`, patch);
            toast.success('Cambios guardados');
            setEditing(false);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al guardar'));
        } finally { setSaving(false); }
    };

    if (loading) {
        return <AdminLayout title="Cliente"><div className="p-12 text-center text-gray-500">Cargando...</div></AdminLayout>;
    }
    if (error || !data) {
        return (
            <AdminLayout title="Cliente">
                <div className="p-12 text-center text-gray-500">
                    <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
                    {error || 'Cliente no encontrado'}
                </div>
            </AdminLayout>
        );
    }

    const { user, totals, groups, certifications, status_counts, progress_by_standard } = data;
    const VIOLET_LOCAL = user.role === 'provider' ? '#E08B3C' : '#7C6BEF';
    const donutData = Object.entries(status_counts)
        .map(([k, v]) => ({ key: k, name: DONUT_LABELS[k], value: v }))
        .filter((d) => d.value > 0);
    const initial = (user.company || user.name || 'C').charAt(0).toUpperCase();
    const logoUrl = user.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.logo_url}` : null;

    return (
        <AdminLayout title={user.company || user.name}>
            <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm font-bold mb-5 hover:underline" style={{ color: VIOLET }}>
                <ArrowLeft className="w-4 h-4" /> Volver a Clientes
            </Link>

            {/* Header card */}
            <div className="p-6 mb-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="client-detail-header">
                <div className="flex items-start gap-5">
                    {logoUrl ? (
                        <img src={logoUrl} alt={user.company || user.name}
                            className="w-20 h-20 object-contain bg-white border shrink-0"
                            style={{ borderColor: 'var(--border)' }}
                            data-testid="client-detail-logo" />
                    ) : (
                        <div className="w-20 h-20 flex items-center justify-center font-display font-black text-3xl shrink-0" style={{ background: `${VIOLET_LOCAL}20`, color: VIOLET_LOCAL }}
                            data-testid="client-detail-avatar">
                            {initial}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h1 className="font-display text-2xl font-black tracking-tight">{user.trade_name || user.company || user.name}</h1>
                            {user.trade_name && user.company && (
                                <span className="text-sm text-gray-500 font-medium">({user.company})</span>
                            )}
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1" style={{ background: user.active ? 'rgba(16,185,129,0.15)' : 'rgba(156,163,175,0.15)', color: user.active ? '#10B981' : '#9CA3AF' }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: user.active ? '#10B981' : '#9CA3AF' }} />
                                {user.active ? 'Activo' : 'Inactivo'}
                            </span>
                            {user.locked_at && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                    style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }}
                                    data-testid="client-locked-badge">
                                    <ShieldAlert className="w-3 h-3" /> Cuenta bloqueada
                                </span>
                            )}
                            <span className="text-xs px-2 py-1 font-bold uppercase tracking-wider" style={{ background: user.role === 'provider' ? 'rgba(224,139,60,0.15)' : 'rgba(31,143,184,0.15)', color: user.role === 'provider' ? '#E08B3C' : '#1F8FB8' }}>
                                {user.role === 'provider' ? 'Proveedor' : 'Cliente principal'}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-gray-600 dark:text-gray-400 mt-3">
                            {user.industry && <InfoRow icon={Briefcase} label={user.industry} />}
                            {user.rfc && <InfoRow icon={IdCard} label={`RFC: ${user.rfc}`} />}
                            {user.email && <InfoRow icon={Mail} label={user.email} />}
                            {user.phone && <InfoRow icon={Phone} label={user.phone} />}
                            {(user.city || user.state) && (
                                <InfoRow icon={MapPin} label={[user.city, user.state, user.country].filter(Boolean).join(', ')} />
                            )}
                            {user.address && <InfoRow icon={MapPin} label={`Op.: ${user.address}`} />}
                            {user.fiscal_address && <InfoRow icon={MapPin} label={`Fiscal: ${user.fiscal_address}`} />}
                            {user.legal_representative && <InfoRow icon={Users} label={`Representante: ${user.legal_representative}`} />}
                            {user.general_manager && <InfoRow icon={Users} label={`Gerente: ${user.general_manager}`} />}
                            {user.property_status && <InfoRow icon={Building2} label={`Predio: ${user.property_status}`} />}
                            {user.work_shifts && (Array.isArray(user.work_shifts) ? user.work_shifts.length > 0 : true) && (
                                <div className="flex items-start gap-2 text-sm">
                                    <Calendar className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Turnos</div>
                                        <WorkShiftsSummary value={user.work_shifts} />
                                    </div>
                                </div>
                            )}
                            {user.contact_person && <InfoRow icon={Users} label={`Contacto: ${user.contact_person}`} />}
                            <InfoRow icon={Calendar} label={`Desde ${formatDate(user.created_at)}`} />
                            {user.latitude && user.longitude && (
                                <a href={`https://www.google.com/maps?q=${user.latitude},${user.longitude}`}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 hover:underline text-sm font-bold" style={{ color: VIOLET_LOCAL }}>
                                    <MapPin className="w-3.5 h-3.5" /> Ver en Google Maps ↗
                                </a>
                            )}
                        </div>
                        {user.locked_at && (
                            <div className="text-xs text-red-600 mt-2" data-testid="client-locked-details">
                                Bloqueada el {formatDate(user.locked_at)} tras 5 intentos fallidos de inicio de sesión
                                {user.last_failed_ip && ` desde IP ${user.last_failed_ip}`}.
                            </div>
                        )}
                        {user.notes && (
                            <div className="text-xs text-gray-500 mt-3 italic border-l-2 pl-3" style={{ borderColor: VIOLET_LOCAL }}>
                                {user.notes}
                            </div>
                        )}
                    </div>
                    <div className="shrink-0 flex flex-col gap-2">
                        <button type="button" onClick={startEdit} data-testid="edit-client-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', background: VIOLET_LOCAL }}
                            className="items-center gap-2 px-4 py-2 text-white font-bold">
                            <Edit2 className="w-4 h-4" /> Editar
                        </button>
                        <button type="button" onClick={() => setShowChecklistPreview(true)}
                            data-testid="open-checklist-preview-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', borderColor: VIOLET_LOCAL, color: VIOLET_LOCAL }}
                            className="items-center gap-2 px-4 py-2 border-2 font-bold text-sm hover:bg-black/5 dark:hover:bg-white/10">
                            <Sparkles className="w-4 h-4" /> Checklist Preview
                        </button>
                        {/*
                         * "+ Nueva auditoría" shortcut — visible ONLY to the logged-in
                         * admin, and ONLY when the profile currently being viewed is a
                         * client (not a provider). This lives inside /admin/clients/:id
                         * which is already gated by <ProtectedRoute role="admin">, so
                         * non-admin sessions can't reach this component. The extra
                         * `sessionUser?.role === 'admin'` check is defense-in-depth for
                         * future refactors — this button must never appear on the
                         * client's own dashboard.
                         */}
                        {sessionUser?.role === 'admin' && user.role === 'client' && (
                            <Link to={`/admin/inspections?openNew=1&clientId=${user.id}`}
                                data-testid="new-audit-shortcut-btn"
                                style={{ display: editing ? 'none' : 'inline-flex', background: VIOLET_LOCAL }}
                                className="items-center gap-2 px-4 py-2 text-white font-bold text-sm hover:opacity-90">
                                <CalendarPlus className="w-4 h-4" /> Nueva auditoría
                            </Link>
                        )}
                        <button type="button" onClick={() => setShowEmailComposer(true)}
                            data-testid="open-email-composer-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', borderColor: 'var(--border)' }}
                            className="items-center gap-2 px-4 py-2 border font-bold text-sm hover:bg-black/5 dark:hover:bg-white/10">
                            <Mail className="w-4 h-4" /> Enviar correo
                        </button>
                        <button type="button" onClick={() => setShowResetPw(true)}
                            data-testid="reset-client-password-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', borderColor: 'var(--border)' }}
                            className="items-center gap-2 px-4 py-2 border font-bold text-sm hover:bg-black/5 dark:hover:bg-white/10">
                            <KeyRound className="w-4 h-4" /> Resetear contraseña
                        </button>
                        {user.locked_at && (
                            <button type="button" onClick={unlock} disabled={unlocking}
                                data-testid="unlock-client-btn"
                                style={{ display: editing ? 'none' : 'inline-flex' }}
                                className="items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold text-sm disabled:opacity-50">
                                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                                Desbloquear
                            </button>
                        )}
                        <button type="button" onClick={() => setShowDelete(true)}
                            data-testid="delete-client-btn"
                            style={{ display: editing ? 'none' : 'inline-flex' }}
                            className="items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 font-bold text-sm hover:bg-red-600 hover:text-white transition-colors">
                            <Trash2 className="w-4 h-4" /> Eliminar {user.role === 'provider' ? 'proveedor' : 'cliente'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Editable panel */}
            <EditPanel
                show={editing} form={form} set={set} user={user}
                onCancel={cancelEdit} onSave={save} saving={saving}
                onCaptureGeo={captureGeo} geoLoading={geoLoading}
                accent={VIOLET_LOCAL}
                rubroOptions={rubroOptions}
                standards={distintivos}
            />

            {/* Iter 73 · Precio personalizado — override manual, siempre visible (no depende de "Editar") */}
            {(user.role === 'client' || user.role === 'provider') && (
                <CustomPriceCard user={user} accent={VIOLET_LOCAL} onUpdated={load} />
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border)' }}>
                <TabButton
                    active={activeTab === 'audits'} onClick={() => setActiveTab('audits')}
                    label="Auditorías y desempeño" testId="tab-audits"
                />
                <TabButton
                    active={activeTab === 'communications'} onClick={() => setActiveTab('communications')}
                    label="Comunicaciones" testId="tab-communications"
                />
                {user.role === 'client' && (
                    <TabButton
                        active={activeTab === 'identity'} onClick={() => setActiveTab('identity')}
                        label="Identidad visual" testId="tab-identity"
                    />
                )}
            </div>

            {activeTab === 'audits' && (
              <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="client-stats">
                <StatCard icon={Clock} label="Programadas" value={totals.scheduled} color="#F59E0B" />
                <StatCard icon={ClipboardCheck} label="En curso" value={totals.in_progress} color="#8B5CF6" />
                <StatCard icon={FileCheck} label="Realizadas" value={totals.completed} color="#3B82F6" />
                <StatCard icon={Award} label="Distintivos" value={totals.certifications} color="#10B981" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="chart-donut">
                    <h3 className="font-display text-lg font-bold mb-1">Distribución de auditorías</h3>
                    <p className="text-xs text-gray-500 mb-4">Estatus actual de todas las auditorías del cliente</p>
                    {donutData.length === 0 ? (
                        <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">Sin auditorías aún</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                                    {donutData.map((d) => <Cell key={d.key} fill={DONUT_COLORS[d.key]} />)}
                                </Pie>
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
                <div className="p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="chart-bar">
                    <h3 className="font-display text-lg font-bold mb-1">Avance promedio por norma</h3>
                    <p className="text-xs text-gray-500 mb-4">Score promedio de cumplimiento (%)</p>
                    {progress_by_standard.length === 0 ? (
                        <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">Aún sin scores registrados</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={progress_by_standard} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                <XAxis dataKey="standard" tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => `${v}%`} />
                                <Bar dataKey="avg_score" fill={VIOLET} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Inspections timeline */}
            <div className="p-6 mb-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="inspections-timeline">
                <h3 className="font-display text-lg font-bold mb-4">Cronología de auditorías</h3>
                {totals.all === 0 ? (
                    <div className="py-8 text-center text-gray-500 text-sm">Este cliente aún no tiene auditorías registradas.</div>
                ) : (
                    <div className="space-y-6">
                        <InspectionsGroup title="Programadas" items={groups.scheduled} emptyMsg="Sin auditorías programadas" distLookup={distLookup} />
                        <InspectionsGroup title="En curso / Revisión" items={groups.in_progress} emptyMsg="Ninguna en curso" distLookup={distLookup} />
                        <InspectionsGroup title="Realizadas" items={groups.completed} emptyMsg="Aún no hay auditorías cerradas" distLookup={distLookup} />
                    </div>
                )}
            </div>

            {/* Certifications */}
            <div className="p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="certifications-list">
                <h3 className="font-display text-lg font-bold mb-4 inline-flex items-center gap-2">
                    <Award className="w-5 h-5" style={{ color: '#10B981' }} /> Distintivos obtenidos
                </h3>
                {certifications.length === 0 ? (
                    <div className="py-6 text-center text-gray-500 text-sm">Aún no obtiene distintivos.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {certifications.map((c) => {
                            const { title, subtitle } = getDisplayTitle(c, distLookup);
                            return (
                            <div key={c.id} className="p-4 border" style={{ borderColor: '#10B981', background: 'rgba(16,185,129,0.05)' }}>
                                <div className="flex items-start justify-between mb-2">
                                    <div className="font-display font-black text-sm">{title}</div>
                                    <Award className="w-4 h-4" style={{ color: '#10B981' }} />
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">{subtitle}</div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Score: <strong style={{ color: '#10B981' }}>{c.score?.toFixed(1)}%</strong></span>
                                    <span className="text-gray-500">{formatDate(c.admin_decided_at || c.updated_at)}</span>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </>
            )}

            {activeTab === 'communications' && (
                <ClientCommunicationsPanel
                    clientId={id}
                    onOpenComposer={() => setShowEmailComposer(true)}
                />
            )}
            {activeTab === 'identity' && user.role === 'client' && (
                <div className="p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="client-identity-panel">
                    <h2 className="font-display text-lg font-bold mb-1">Identidad visual del negocio</h2>
                    <p className="text-sm text-gray-500 mb-5">
                        Logotipo y fotos del establecimiento del cliente. Se mostrarán en su ficha pública cuando obtenga un distintivo.
                    </p>
                    <ClientMediaPanel userId={id} editable={true} />
                </div>
            )}
            {showResetPw && (
                <ResetPasswordModal
                    userId={id}
                    userLabel={user?.name || user?.email}
                    accent={VIOLET_LOCAL}
                    onClose={() => setShowResetPw(false)}
                />
            )}
            {showChecklistPreview && (
                <ChecklistPreviewModal
                    clientId={id}
                    clientLabel={user?.trade_name || user?.company || user?.name}
                    onClose={() => setShowChecklistPreview(false)}
                />
            )}
            {showEmailComposer && (
                <EmailComposerModal
                    clientId={id}
                    clientLabel={user?.trade_name || user?.company || user?.name}
                    clientEmail={user?.email}
                    distintivos={distintivos}
                    onClose={() => setShowEmailComposer(false)}
                />
            )}
            {showDelete && (
                <DeleteClientModal
                    user={user}
                    totals={totals}
                    onClose={() => setShowDelete(false)}
                    onDeleted={() => {
                        setShowDelete(false);
                        navigate('/admin/clients', { replace: true });
                    }}
                />
            )}
        </AdminLayout>
    );
}

function InfoRow({ icon: Icon, label }) {
    return (
        <div className="inline-flex items-center gap-2 truncate">
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{label}</span>
        </div>
    );
}

/**
 * Iter 73 · override manual de precio por cliente. Cuando está definido,
 * SIEMPRE tiene prioridad sobre el cálculo por rango, sin importar tamaño o
 * rubro. Solo visible/editable del lado admin — el cliente nunca ve la
 * etiqueta "personalizado", solo el precio final en su cotización.
 */
function CustomPriceCard({ user, accent, onUpdated }) {
    const [value, setValue] = useState(user.custom_price_mxn ?? '');
    const [saving, setSaving] = useState(false);
    const hasCustom = user.custom_price_mxn != null;

    useEffect(() => { setValue(user.custom_price_mxn ?? ''); }, [user.custom_price_mxn]);

    const save = async () => {
        const parsed = value === '' ? null : Number(value);
        if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) { toast.error('Precio inválido'); return; }
        setSaving(true);
        try {
            await api.patch(`/admin/users/${user.id}/custom-price`, { custom_price_mxn: parsed });
            toast.success('Precio personalizado guardado');
            onUpdated();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar'));
        } finally { setSaving(false); }
    };

    const clear = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/users/${user.id}/custom-price`, { custom_price_mxn: null });
            toast.success('Precio personalizado removido — vuelve al cálculo automático');
            onUpdated();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al quitar'));
        } finally { setSaving(false); }
    };

    return (
        <div className="p-5 mb-6 border-2" style={{ borderColor: hasCustom ? accent : 'var(--border)', background: 'var(--card-bg)' }} data-testid="custom-price-card">
            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>Precio personalizado</div>
            <p className="text-xs text-gray-500 mb-3 max-w-lg">
                Si defines un valor, tiene prioridad SIEMPRE sobre el cálculo por rango, para cualquier solicitud de este cliente.
                El cliente solo ve el precio final — nunca esta etiqueta.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="opacity-60 text-sm">$</span>
                <input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)}
                    placeholder="Sin definir (cálculo automático)" data-testid="custom-price-input"
                    className="w-56 px-3 py-2 border-2 outline-none text-sm" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                <span className="opacity-60 text-sm">MXN</span>
                <button type="button" onClick={save} disabled={saving} data-testid="custom-price-save-btn"
                    className="px-4 py-2 text-white font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: accent }}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar
                </button>
                {hasCustom && (
                    <button type="button" onClick={clear} disabled={saving} data-testid="custom-price-clear-btn"
                        className="px-4 py-2 border font-bold text-sm disabled:opacity-50" style={{ borderColor: 'var(--border)' }}>
                        Quitar
                    </button>
                )}
            </div>
            {hasCustom && (
                <div className="mt-3 text-xs px-3 py-2 inline-flex items-center gap-1.5" style={{ background: `${accent}12`, color: accent }} data-testid="custom-price-trace">
                    Precio personalizado activo: ${user.custom_price_mxn} MXN — definido por {user.custom_price_set_by || '—'} el {formatDateTime(user.custom_price_set_at)}
                </div>
            )}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="p-5" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${color}22`, color }}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div className="font-display text-3xl font-black" style={{ color }}>{value}</div>
            <div className="text-xs uppercase tracking-wider font-bold text-gray-500 mt-1">{label}</div>
        </div>
    );
}

function InspectionsGroup({ title, items, emptyMsg, distLookup }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3 text-gray-500">{title} <span className="text-gray-400">({items.length})</span></div>
            {items.length === 0 ? (
                <div className="text-xs text-gray-400 italic py-2">{emptyMsg}</div>
            ) : (
                <div className="space-y-2">
                    {items.map((r) => {
                        const { title: rTitle } = getDisplayTitle(r, distLookup);
                        return (
                        <Link key={r.id} to={`/admin/certifications`} className="flex items-center justify-between gap-3 p-3 hover:bg-black/[0.03] dark:hover:bg-white/5 border" style={{ borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-1 h-10 shrink-0" style={{ background: STATUS_STYLES[r.status]?.color || '#999' }} />
                                <div className="min-w-0">
                                    <div className="font-bold text-sm truncate">{rTitle} <span className="text-gray-500 font-normal">— {r.industry}</span></div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Creada {formatDate(r.created_at)} {r.inspector_name && `· Auditor: ${r.inspector_name}`}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                {r.score !== null && r.score !== undefined && (
                                    <span className="text-sm font-bold">{r.score.toFixed(1)}%</span>
                                )}
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                    style={{ background: STATUS_STYLES[r.status]?.bg, color: STATUS_STYLES[r.status]?.color }}>
                                    {STATUS_STYLES[r.status]?.label || r.status}
                                </span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                            </div>
                        </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/**
 * Panel editable del Cliente/Proveedor. DOM-stable: siempre renderiza inputs,
 * sólo toggle de visibilidad por estilo. Evita 'insertBefore NotFoundError' en React 19.
 */
function EditPanel({ show, form, set, user, onCancel, onSave, saving, onCaptureGeo, geoLoading, accent, rubroOptions, standards: distintivos = [] }) {
    if (!show) return null;
    return (
        <div className="p-6 mb-6" style={{ background: 'var(--card-bg)', border: `2px solid ${accent}` }} data-testid="client-edit-panel">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Editando</div>
                    <h3 className="font-display text-xl font-black">Datos del {user.role === 'provider' ? 'Proveedor' : 'Cliente'}</h3>
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={onCancel}
                        className="inline-flex items-center gap-2 px-4 py-2 border font-bold"
                        style={{ borderColor: 'var(--border)' }}>
                        <X className="w-4 h-4" /> Cancelar
                    </button>
                    <button type="button" onClick={onSave} disabled={saving} data-testid="save-client-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold disabled:opacity-50"
                        style={{ background: accent }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Guardar
                    </button>
                </div>
            </div>

            {/* Datos generales */}
            <FormSection title="Datos generales" accent={accent}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormInput label="Nombre comercial" v={form.trade_name} on={(v) => set('trade_name', v)} placeholder="Marca / nombre comercial" />
                    <FormInput label="Razón social" v={form.company} on={(v) => set('company', v)} placeholder="Empresa S.A. de C.V." />
                    <FormInput label="RFC" v={form.rfc} on={(v) => set('rfc', (v || '').toUpperCase())} placeholder="12 o 13 caracteres" mono maxLength={13} />
                    <FormInput label="Teléfono" v={form.phone} on={(v) => set('phone', v)} placeholder="(+52) ..." />
                    <FormSelect label="Rubro / Giro" v={form.industry} on={(v) => set('industry', v)} options={rubroOptions || INDUSTRIES} placeholder="Selecciona rubro" />
                    <FormMultiCert label="Distintivos deseados" values={form.desired_distintivo_ids || []} onChange={(arr) => set('desired_distintivo_ids', arr)} distintivos={distintivos} />
                    <FormSelect label="Situación del predio" v={form.property_status} on={(v) => set('property_status', v)} options={PROPERTY_STATUS_OPTIONS} placeholder="Selecciona situación" />
                </div>
            </FormSection>

            {/* Direcciones */}
            <FormSection title="Direcciones" accent={accent}>
                <FormInput label="Dirección operativa" v={form.address} on={(v) => set('address', v)} placeholder="Calle, número, colonia (planta/sucursal)" />
                <FormInput label="Dirección fiscal" v={form.fiscal_address} on={(v) => set('fiscal_address', v)} placeholder="Domicilio fiscal" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FormInput label="Ciudad" v={form.city} on={(v) => set('city', v)} />
                    <FormInput label="Estado" v={form.state} on={(v) => set('state', v)} />
                    <FormInput label="País" v={form.country} on={(v) => set('country', v)} />
                </div>
            </FormSection>

            {/* Representación */}
            <FormSection title="Representación y operación" accent={accent}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormInput label="Persona de contacto" v={form.name} on={(v) => set('name', v)} placeholder="Responsable principal" />
                    <FormInput label="Representante legal" v={form.legal_representative} on={(v) => set('legal_representative', v)} />
                    <FormInput label="Gerente general" v={form.general_manager} on={(v) => set('general_manager', v)} />
                </div>
            </FormSection>

            {/* Turnos */}
            <FormSection title="Turnos laborales" accent={accent}>
                <WorkShiftsField
                    value={form.work_shifts}
                    onChange={(v) => set('work_shifts', v)}
                    testIdPrefix="edit-client-shift"
                />
            </FormSection>

            {/* Iter 73 · Datos de tamaño para cotización por rango */}
            <FormSection title="Datos para cotización (tamaño del negocio)" accent={accent}>
                <p className="text-xs text-gray-500 -mt-1 mb-2">
                    Usados para calcular el precio por rango según el rubro. El cliente los captura en su panel — aquí puedes corregirlos.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormInput label="Número de habitaciones" v={form.num_habitaciones} on={(v) => set('num_habitaciones', v)} placeholder="Hotelería" />
                    <FormInput label="Número de mesas" v={form.num_mesas} on={(v) => set('num_mesas', v)} placeholder="Restaurantes" />
                    <FormInput label="Número de colaboradores" v={form.capacity_collaborators} on={(v) => set('capacity_collaborators', v)} placeholder="Servicios Profesionales" />
                    <FormInput label="Metros cuadrados de construcción" v={form.m2_construccion} on={(v) => set('m2_construccion', v)} placeholder="Todos los demás rubros" />
                </div>
            </FormSection>

            {/* Geolocalización */}
            <FormSection title="Geolocalización" accent={accent}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <FormInput label="Latitud" v={form.latitude} on={(v) => set('latitude', v)} placeholder="21.1619" />
                    <FormInput label="Longitud" v={form.longitude} on={(v) => set('longitude', v)} placeholder="-86.8515" />
                    <button type="button" onClick={onCaptureGeo} disabled={geoLoading}
                        className="text-white px-4 py-2.5 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: accent }}>
                        {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                        {geoLoading ? 'Obteniendo...' : 'Capturar GPS'}
                    </button>
                </div>
                {form.latitude && form.longitude && (
                    <div className="text-xs text-gray-500 mt-2">
                        📍 {form.latitude}, {form.longitude} ·{' '}
                        <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer" className="underline" style={{ color: accent }}>
                            Ver en Google Maps ↗
                        </a>
                    </div>
                )}
            </FormSection>

            {/* Notas */}
            <FormSection title="Notas internas" accent={accent}>
                <textarea rows={3} value={form.notes || ''}
                    onChange={(e) => set('notes', e.target.value)}
                    placeholder="Anotaciones internas (no visibles para el cliente)"
                    className="w-full px-3 py-2 border outline-none resize-none text-sm"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
            </FormSection>
        </div>
    );
}

function FormSection({ title, accent, children }) {
    return (
        <div className="mb-5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: accent }}>{title}</div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function FormInput({ label, v, on, placeholder, mono, maxLength }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-gray-500">{label}</label>
            <input type="text" value={v ?? ''} onChange={(e) => on(e.target.value)}
                placeholder={placeholder} maxLength={maxLength}
                className={`w-full px-3 py-2 border outline-none text-sm ${mono ? 'font-mono' : ''}`}
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
        </div>
    );
}

function FormSelect({ label, v, on, options, placeholder }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-gray-500">{label}</label>
            <select value={v ?? ''} onChange={(e) => on(e.target.value)}
                className="w-full px-3 py-2 border outline-none text-sm"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)', color: 'var(--foreground)' }}>
                <option value="">{placeholder || 'Selecciona'}</option>
                {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
    );
}


/**
 * Modal de confirmación para SOFT-DELETE de cliente/proveedor (Iter 43).
 * - Requiere escribir la razón social (o nombre) para confirmar (protección extra).
 * - Backend hace soft-delete: marca `deleted:true`, revoca sesiones, preserva
 *   auditorías, certificados y hallazgos. Restaurable desde AdminClients con el
 *   toggle "Ver eliminados".
 */
function DeleteClientModal({ user, totals, onClose, onDeleted }) {
    const label = user?.trade_name || user?.company || user?.name || '';
    const roleLabel = user?.role === 'provider' ? 'proveedor' : 'cliente';
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const hasRelated = (totals?.all || 0) > 0 || (totals?.certifications || 0) > 0;
    const confirmMatches = confirmText.trim() === label.trim() && label.length > 0;

    const doDelete = async () => {
        if (!confirmMatches) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${user.id}`);
            toast.success(`${roleLabel === 'proveedor' ? 'Proveedor' : 'Cliente'} eliminado (recuperable desde "Ver eliminados")`);
            onDeleted();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al eliminar'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="delete-client-modal"
                style={{ background: 'var(--card-bg)', border: '2px solid #DC2626' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(220,38,38,0.15)' }}>
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-red-600">Acción reversible</div>
                            <h2 className="font-display text-xl font-black">Eliminar {roleLabel}</h2>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        Estás por eliminar a:
                    </p>
                    <div className="border-l-4 pl-4 py-2 bg-red-50 dark:bg-red-950/20" style={{ borderColor: '#DC2626' }}>
                        <div className="font-display text-lg font-black">{label}</div>
                        {user.email && <div className="text-xs text-gray-500">{user.email}</div>}
                    </div>

                    {hasRelated && (
                        <div className="border p-3 text-xs" style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)' }}>
                            <div className="font-bold text-amber-700 dark:text-amber-500 mb-1 inline-flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> Este {roleLabel} tiene datos asociados
                            </div>
                            <ul className="text-gray-600 dark:text-gray-400 space-y-0.5 ml-1">
                                {(totals?.scheduled ?? 0) > 0 && <li>• {totals.scheduled} auditorías programadas</li>}
                                {(totals?.in_progress ?? 0) > 0 && <li>• {totals.in_progress} en curso</li>}
                                {(totals?.completed ?? 0) > 0 && <li>• {totals.completed} realizadas</li>}
                                {(totals?.certifications ?? 0) > 0 && <li>• {totals.certifications} distintivos</li>}
                            </ul>
                            <div className="text-gray-500 mt-2">
                                Al eliminar, todos estos registros <strong>se conservan intactos</strong>. Puedes restaurar
                                al {roleLabel} en cualquier momento desde el listado (toggle &quot;Ver eliminados&quot;).
                            </div>
                        </div>
                    )}

                    <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                        Se marca la cuenta como eliminada y se revocan sus sesiones activas. Auditorías,
                        distintivos, hallazgos e historial permanecen intactos. Restaurable en un click.
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">
                            Para confirmar, escribe el nombre exactamente: <span className="text-red-600">{label}</span>
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            data-testid="delete-client-confirm-input"
                            placeholder={label}
                            className="w-full px-3 py-2 border-2 outline-none font-mono text-sm"
                            style={{
                                borderColor: confirmMatches ? '#10B981' : 'var(--border)',
                                background: 'var(--card-bg)',
                            }}
                            autoFocus
                        />
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex gap-2 justify-end" style={{ borderColor: 'var(--border)' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={deleting}
                        className="px-5 py-2 border font-bold hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={doDelete}
                        disabled={!confirmMatches || deleting}
                        data-testid="delete-client-confirm-btn"
                        className="px-6 py-2 text-white font-bold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: '#DC2626' }}
                    >
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar {roleLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TabButton({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors relative"
            style={{
                color: active ? VIOLET : 'inherit',
                opacity: active ? 1 : 0.55,
                borderBottom: active ? `2px solid ${VIOLET}` : '2px solid transparent',
                marginBottom: '-1px',
            }}
        >
            {label}
        </button>
    );
}



function FormMultiCert({ label, values = [], onChange, distintivos = [] }) {
    const [open, setOpen] = React.useState(false);
    const selected = distintivos.filter((s) => values.includes(s.id));
    // Only allow selecting from ACTIVE ones (inactive already-picked stay in "selected" chips).
    const selectableDistintivos = distintivos.filter((s) => s.is_active !== false || values.includes(s.id));
    const toggle = (id) => onChange(values.includes(id) ? values.filter((x) => x !== id) : [...values, id]);
    return (
        <div className="relative">
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <button type="button" onClick={() => setOpen((o) => !o)}
                data-testid="clientdetail-multi-cert-toggle"
                className="w-full px-3 py-2 border outline-none text-left flex items-center justify-between text-sm"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <span className="truncate">
                    {selected.length === 0 ? <span className="text-gray-400">Ninguno seleccionado</span>
                        : selected.length === 1 ? selected[0].code
                        : `${selected.length} seleccionados`}
                </span>
                <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
            </button>
            {selected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {selected.map((s) => {
                        const inactive = s.is_active === false;
                        return (
                            <span key={s.id} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5" style={{ background: inactive ? 'rgba(220,38,38,0.15)' : `${VIOLET}22`, color: inactive ? '#DC2626' : VIOLET }} title={inactive ? 'Distintivo desactivado del catálogo' : ''}>
                                {s.code}{inactive ? ' (inactivo)' : ''}
                                <button type="button" onClick={() => toggle(s.id)} className="hover:opacity-70" data-testid={`clientdetail-cert-remove-${s.id}`}>
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
            {open && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto border shadow-lg" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    {selectableDistintivos.length === 0 ? (
                        <div className="p-3 text-xs text-gray-400 italic">Cargando…</div>
                    ) : (
                        selectableDistintivos.map((s) => {
                            const inactive = s.is_active === false;
                            return (
                                <label key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer border-b last:border-b-0" style={{ borderColor: 'var(--border)', opacity: inactive ? 0.6 : 1 }}>
                                    <input type="checkbox" checked={values.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`clientdetail-cert-check-${s.id}`} />
                                    <span className="font-bold">{s.code}</span>
                                    <span className="text-xs text-gray-500 truncate">{s.name}</span>
                                    {inactive && <span className="text-[9px] font-bold text-red-500 ml-auto">INACTIVO</span>}
                                </label>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}
